import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setBetBuilderModalShow,
  addBetToBetSlip,
  clearBetBuilderSelections,
  removeBetBuilderSelection
} from 'stores/betSlip';
import { MarketIdToMarketName, OddsValueTypeTextCreator } from 'toolkit/utils';
import { getTeamNameHelper } from 'helper/matchDetailHelper';
import { XLg } from 'react-bootstrap-icons';
import { BET_BUILDER_STATUS } from 'toolkit/betBuilder';

function BetBuilderModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations', { useSuspense: false });
  const betBuilder = useSelector((state) => state.betSlip.betBuilder);
  const betSlip = useSelector((state) => state.betSlip);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const match = useSelector((state) => state.match.events[betBuilder.selections[0]?.providerId]);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    dispatch(setBetBuilderModalShow(false));
  };

  const getUniqueMarketsCount = () => {
    // Count unique markets based on marketStringId (which includes specifiers)
    const uniqueMarkets = new Set();
    betBuilder.selections.forEach(selection => {
      // Use marketStringId as the unique identifier for a market
      uniqueMarkets.add(selection.marketStringId);
    });
    return uniqueMarkets.size;
  };

  const hasMinimumMarkets = () => {
    return getUniqueMarketsCount() >= 2;
  };

  // Only MyOdds prices a custom bet; without its price the selection cannot go to the bet slip
  const isPriced = betBuilder.status === BET_BUILDER_STATUS.OK && betBuilder.calculatedOdds > 1;
  const canAddToBetSlip = hasMinimumMarkets() && isPriced;

  const statusMessage = {
    [BET_BUILDER_STATUS.NOT_COMBINABLE]: t('betBuilder.notCombinable'),
    [BET_BUILDER_STATUS.RATE_LIMITED]: t('betBuilder.rateLimited'),
    [BET_BUILDER_STATUS.UNAVAILABLE]: t('betBuilder.unavailable')
  }[betBuilder.status];

  const handleAddToBetSlip = () => {
    // Validate minimum 2 markets requirement and a MyOdds price
    if (!canAddToBetSlip) {
      return;
    }

    // Check if there's already a normal bet for this event
    const providerId = betBuilder.selections[0]?.providerId;
    if (providerId) {
      const hasNormalBet = betSlip.bets.some(
        (b) => b.providerId === providerId && !b.isBetBuilder
      );
      if (hasNormalBet) {
        return;
      }
    }

    // Generate a unique BetBuilderId for this custom bet (all selections from this modal click)
    // Use Redux state directly for reliable betBuilderId generation
    const existingBetBuilderIds = betSlip.bets
      .filter((b) => b.isBetBuilder && b.betBuilderId !== undefined)
      .map((b) => b.betBuilderId);
    const newBetBuilderId = existingBetBuilderIds.length > 0 ? Math.max(...existingBetBuilderIds) + 1 : 1;

    // Get the calculated odds from API response
    const calculatedOdds = betBuilder.calculatedOdds;

    // Add all bet builder selections to the main betslip with the same BetBuilderId and calculatedOdds
    betBuilder.selections.forEach(selection => {
      dispatch(addBetToBetSlip({
        providerId: selection.providerId,
        marketStringId: selection.marketStringId,
        marketId: selection.marketId,
        outcomeId: selection.outcomeId,
        outcomeName: selection.outcomeName,
        odds: selection.odds,
        match: selection.match,
        isBetBuilder: true,
        betBuilderId: newBetBuilderId,
        betBuilderCalculatedOdds: calculatedOdds
      }));
    });

    // Clear bet builder selections, close modal, and disable bet builder mode
    dispatch(clearBetBuilderSelections());
    dispatch(setBetBuilderModalShow(false));
    setIsExpanded(false);
  };

  const handleRemoveSelection = (providerId, marketStringId) => {
    dispatch(removeBetBuilderSelection({ providerId, marketStringId }));
  };

  const totalOddsText = () => {
    if (betBuilder.status === BET_BUILDER_STATUS.LOADING) return '…';
    return isPriced ? Number(betBuilder.calculatedOdds).toFixed(2) : '-';
  };

  // Render bottom sheet when bet builder is enabled and there are selections,
  // or when explicitly asked to show it (collapsed state).
  if (!betBuilder.isEnabled || (betBuilder.selections.length === 0 && !betBuilder.showModal)) {
    return null;
  }

  const modalContent = (
    <div
      className={`bet-builder-modal-overlay show ${isExpanded ? 'expanded' : 'collapsed'}`}
    >
      <div
        className={`bet-builder-modal-content ${isExpanded ? 'expanded' : 'collapsed'}`}
      >
        <div className="bet-builder-modal-header">
          <div className="bet-builder-drag-handle" onClick={handleToggle}></div>
          <div className="d-flex justify-content-between align-items-center">
            {isExpanded ? (""
            ) : (
              <>
                <div className="d-flex align-items-center" onClick={handleToggle} style={{ cursor: 'pointer' }}>
                  <div className="bet-builder-selections-count me-2">
                    {betBuilder.selections.length}
                  </div>
                  <span className="bet-builder-selections-text">
                    {t('betBuilder.selections', 'Selections')}
                  </span>
                  {statusMessage && (
                    <small className="bet-builder-status-error ms-2">{statusMessage}</small>
                  )}
                  <i className={`fas fa-chevron-${isExpanded ? 'down' : 'up'} bet-builder-arrow ms-2`}></i>
                </div>
                <div
                  className={`bet-builder-total-odds ${canAddToBetSlip ? 'clickable' : 'disabled'}`}
                  onClick={canAddToBetSlip ? handleAddToBetSlip : undefined}
                  role="button"
                  aria-disabled={!canAddToBetSlip}
                  style={{
                    cursor: canAddToBetSlip ? 'pointer' : 'not-allowed',
                    opacity: canAddToBetSlip ? 1 : 0.5
                  }}
                >
                  {totalOddsText()}
                </div>
              </>
            )}
            {isExpanded && (
              <button
                className="bet-builder-collapse-btn"
                onClick={handleCollapse}
                aria-label={t('betBuilder.collapse', 'Collapse')}
              >
                <XLg size={16} />
              </button>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="bet-builder-modal-body">
            {betBuilder.selections.length === 0 ? (
              <div className="text-center py-4">
                <div className="mb-2">
                  <i className="fas fa-plus-circle" style={{ fontSize: '32px', color: '#28a745' }}></i>
                </div>
                <h6 className="text-muted mb-1">{t('betBuilder.noSelections', 'No selections added yet')}</h6>
                <p className="text-muted small mb-0">{t('betBuilder.addSelectionsHint', 'Add selections from the betting markets above')}</p>
              </div>
            ) : (
              <div>
                {betBuilder.selections.map((selection, index) => (
                  <div key={`${selection.providerId}-${selection.marketStringId}-${selection.outcomeId}-${index}`} className="bet-builder-selection-item">
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-start">
                          <div className="bet-builder-selection-connector">
                            <div className="bet-builder-selection-dot filled"></div>
                          </div>
                          <div className="bet-builder-selection-content">
                            <span className="bet-builder-selection-market">
                              {MarketIdToMarketName(selection.marketId, selection.marketStringId, false)}
                            </span>
                            <span className="bet-builder-selection-separator">: </span>
                            <span className="bet-builder-selection-title">
                              {match ? OddsValueTypeTextCreator(
                                match.sport.toString(),
                                selection.marketId.toString(),
                                selection.outcomeId,
                                selection.outcomeName
                              ) : selection.outcomeName}
                            </span>
                          </div>
                        </div>
                      </div>
                        <div className="d-flex align-items-center">
                          <button
                            className="bet-builder-remove-btn"
                            onClick={() => handleRemoveSelection(selection.providerId, selection.marketStringId)}
                          >
                            <XLg size={18} />
                          </button>
                        </div>
                    </div>
                  </div>
                ))}

                {statusMessage && (
                  <div className="bet-builder-warning-message bet-builder-status-error text-center py-2">
                    <small>{statusMessage}</small>
                  </div>
                )}

                {!hasMinimumMarkets() && betBuilder.selections.length > 0 && (
                  <div className="bet-builder-warning-message text-center py-2">
                    <small className="text-muted">
                      {t('betBuilder.minimumMarketsRequired', 'Minimum 2 different markets required to add to betslip')}
                    </small>
                  </div>
                )}

                <div className="bet-builder-summary-bar">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="bet-builder-summary-count">
                        {betBuilder.selections.length}
                      </div>
                      <span className="bet-builder-summary-text">
                        {t('betBuilder.selections', 'Selections')}
                      </span>
                      <i className="fas fa-chevron-down bet-builder-summary-chevron"></i>
                    </div>
                    <div
                      className={`bet-builder-summary-odds ${canAddToBetSlip ? 'clickable' : 'disabled'}`}
                      onClick={canAddToBetSlip ? handleAddToBetSlip : undefined}
                      style={{
                        cursor: canAddToBetSlip ? 'pointer' : 'not-allowed',
                        opacity: canAddToBetSlip ? 1 : 0.5
                      }}
                    >
                      {totalOddsText()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default BetBuilderModal;
